if (!customElements.get('product-size-guide')) {
  customElements.define(
    'product-size-guide',
    class ProductSizeGuide extends HTMLElement {
      constructor() {
        super();
        this.onKeydown = this.onKeydown.bind(this);
        this.currentUnit = 'cm';
        this.measurements = [];
        this.displayMeasurements = [];
        this.rows = [];
        this.unitConversionMode = 'all';
        this.hasLoaded = false;
        this.cachedMetaobject = null;
        this.loadPromise = null;
        this.openedBy = null;
      }

      connectedCallback() {
        // chart modal
        this.chartModal = this.querySelector('[data-sg-chart-modal]');
        this.chartDialog = this.querySelector('[data-sg-dialog]');
        this.loadingElement = this.querySelector('[data-size-guide-loading]');
        this.errorElement = this.querySelector('[data-size-guide-error]');
        this.contentElement = this.querySelector('[data-size-guide-content]');
        this.fallbackElement = this.querySelector('[data-size-guide-fallback]');
        this.unitsElement = this.querySelector('[data-size-guide-units]');
        this.unitButtons = Array.from(this.querySelectorAll('[data-size-guide-unit]'));
        this.imageWrapElement = this.querySelector('[data-size-guide-image-wrap]');
        this.imageElement = this.querySelector('[data-size-guide-image]');
        this.tableWrapElement = this.querySelector('[data-size-guide-table-wrap]');
        this.tableElement = this.querySelector('[data-size-guide-table]');
        this.chartTabs = Array.from(this.querySelectorAll('[data-sg-ctab]'));
        this.chartPanels = Array.from(this.querySelectorAll('[data-sg-cpanel]'));

        // "How to Measure" numbered list (built from the chart's own columns)
        this.measureListEl = this.querySelector('[data-size-guide-measure-list]');
        this.measureIntroEl = this.querySelector('[data-size-guide-measure-intro]');
        this.measureI18n = {};
        const measureI18nEl = this.querySelector('[data-sg-measure-i18n]');
        if (measureI18nEl) {
          try {
            this.measureI18n = JSON.parse(measureI18nEl.textContent || '{}');
          } catch (error) {
            this.measureI18n = {};
          }
        }
        // localized chart-column display names, keyed by lowercased English column
        // name (e.g. "chest width" -> "Brustumfang" on DE). Identity on English
        // stores, so raw chart column names pass through unchanged.
        this.measureNames = {};
        const measureNamesEl = this.querySelector('[data-sg-measure-names]');
        if (measureNamesEl) {
          try {
            this.measureNames = JSON.parse(measureNamesEl.textContent || '{}');
          } catch (error) {
            this.measureNames = {};
          }
        }

        // triggers
        this.openChartBtn = this.querySelector('[data-sg-open-chart]');
        this.openRecBtn = this.querySelector('[data-sg-open-rec]');

        this.sizeOrder = (this.dataset.sizeOrder || '2XS,XS,S,M,L,XL,2XL,3XL,4XL,5XL')
          .split(',')
          .map((size) => size.trim().toUpperCase())
          .filter(Boolean);

        this.unitButtons.forEach((button) =>
          button.addEventListener('click', () => this.setUnit(button.dataset.sizeGuideUnit))
        );
        this.setupChartTabs();
        this.setupChartModalControls();

        // open the chart modal from the SIZE GUIDE link
        this.openChartBtn?.addEventListener('click', () => this.openChart('guide'));

        // size recommender (separate modal), shares this element's fetched chart
        this.recommender = null;
        const recRoot = this.querySelector('[data-size-recommender]');
        if (recRoot && window.SizeRecommender) {
          try {
            this.recommender = new window.SizeRecommender(recRoot, this);
          } catch (error) {
            this.recommender = null;
          }
        }
        if (this.openRecBtn) {
          if (this.recommender) {
            this.openRecBtn.addEventListener('click', () => this.recommender.open(this.openRecBtn));
          } else {
            this.openRecBtn.hidden = true;
          }
        }

        if (this.dataset.handle) this.prefetch();
        this.setupTryOn();
        // links that can't share the line with the size (at every size) stack under each other
        const field = this.closest('.product-form__input');
        if (field) this.classList.toggle('product-size-guide--stacked', !this.rowFitsOneLine(field));
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
      }

      // Virtual try-on (Genlook app block, EU + US + DE): main-product__variant-picker.liquid renders an
      // outlined "Virtual Try-On" button on its own row under the size buttons (owner 2026-10-21). It takes
      // the app's own label and clicks the app's button, which section-product.css hides, so the app keeps
      // owning the try-on flow. Without the app's button the row is hidden.
      setupTryOn() {
        const field = this.closest('.product-form__input');
        const row = field?.nextElementSibling;
        if (!row || !row.matches('[data-sg-try-on-row]') || this.tryOnButton) return;
        const scope = this.closest('.product__content');
        const button = row.querySelector('[data-sg-try-on]');
        const appButton = scope?.querySelector('.genlook-button');
        if (!button || !appButton) {
          row.hidden = true;
          return;
        }

        const label =
          (appButton.querySelector('.genlook-button-text')?.textContent || '').trim() ||
          (appButton.getAttribute('aria-label') || '').trim();
        const labelNode = button.querySelector('[data-sg-try-on-label]');
        if (label && labelNode) labelNode.textContent = label;
        // look the app button up again on click: a variant change may have re-rendered it
        button.addEventListener('click', () => (scope.querySelector('.genlook-button') || appButton).click());
        this.tryOnButton = button;
      }

      // Does the size row (label, size, links) fit on one line? Find my fit counts before its
      // chart has loaded and the longest size name stands in for the selected one, so every
      // size gets the same answer (a variant change re-renders this element).
      rowFitsOneLine(field) {
        const line = this.closest('legend');
        const value = line?.querySelector('[data-selected-value]');
        if (!value) return true;
        const fit = this.recommender ? this.openRecBtn : null;
        const fitHidden = fit?.hidden;
        if (fit) fit.hidden = false;
        const probe = value.cloneNode();
        probe.textContent = Array.from(field.querySelectorAll('.product-option__input'), (input) => input.value).reduce(
          (longest, name) => (name.length > longest.length ? name : longest),
          value.textContent.trim()
        );
        probe.style.cssText = 'position: absolute; visibility: hidden; white-space: nowrap;';
        value.after(probe);
        const available = line.getBoundingClientRect().width;
        this.setAttribute('data-measuring', '');

        // Unwrapped, the row grows to its content (the fieldset to its min-content) or overflows;
        // the auto margin between the size and the links is spare room (LTR or RTL). Box widths
        // are fractional; scrollWidth alone is rounded and missed sub-pixel overflows.
        const lineBox = line.getBoundingClientRect();
        const linksBox = this.getBoundingClientRect();
        const sizeBox = value.getBoundingClientRect();
        const spare = Math.max(0, linksBox.left - sizeBox.right, sizeBox.left - linksBox.right);
        const longer = Math.max(0, probe.getBoundingClientRect().width - sizeBox.width);
        const fits = Math.max(lineBox.width, line.scrollWidth) - spare + longer <= available;

        this.removeAttribute('data-measuring');
        probe.remove();
        if (fit) fit.hidden = fitHidden;
        return fits;
      }

      // ---- data ----
      // Single in-flight metaobject fetch shared by prefetch() and
      // ensureLoaded() — a fast click used to fire a SECOND identical
      // cross-origin GraphQL POST while the prefetch was still pending.
      fetchMetaobjectOnce() {
        if (this.cachedMetaobject) return Promise.resolve(this.cachedMetaobject);
        if (!this.metaobjectPromise) {
          this.metaobjectPromise = this.fetchMetaobject()
            .then((metaobject) => {
              this.cachedMetaobject = metaobject;
              return metaobject;
            })
            .catch((error) => {
              this.metaobjectPromise = null; // allow retry after a transient failure
              throw error;
            });
        }
        return this.metaobjectPromise;
      }

      async prefetch() {
        try {
          await this.fetchMetaobjectOnce();
          // Decide recommender availability early (hide "Find my size" for
          // charts with no body-circumference columns, e.g. accessories).
          if (this.recommender) {
            const fields = new Map((this.cachedMetaobject.fields || []).map((f) => [f.key, f]));
            const field = fields.get('measurements') || fields.get('measurments');
            const measurements = this.parseMeasurements(field?.value);
            if (measurements.length) {
              const applicable = this.recommender.applyChart(measurements, { sizeOrder: this.sizeOrder });
              if (applicable && this.openRecBtn) this.openRecBtn.hidden = false;
              if (applicable && typeof this.recommender.maybeAutoCompute === 'function') {
                try {
                  this.recommender.maybeAutoCompute();
                } catch (error) {
                  /* auto-compute is best-effort */
                }
              }
            }
          }
        } catch (error) {
          // FAIL OPEN: a transient network failure must not hide the feature
          // for the whole page view — show the link; opening retries via
          // ensureLoaded() and surfaces a real error state if it fails again.
          if (this.recommender && this.openRecBtn) this.openRecBtn.hidden = false;
        }
      }

      // Loads + parses the chart once; resolves with the parsed measurements.
      ensureLoaded() {
        if (this.loadPromise) return this.loadPromise;
        this.loadPromise = (async () => {
          this.setLoadingState();
          const metaobject = await this.fetchMetaobjectOnce();
          this.populate(metaobject);
          this.hasLoaded = true;
          return this.measurements;
        })().catch((error) => {
          console.error('Size guide could not be loaded.', error);
          this.setErrorState();
          this.loadPromise = null; // allow retry
          throw error;
        });
        return this.loadPromise;
      }

      // A style family keeps one chart per fit standard: "wk1073" (base) and
      // "wk1073d" list the same D measurements, "wk1073a" the A ones. When a
      // product's own code has no record (the base was deleted when the family
      // was split, or only one of the two was ever made) its D twin is the same
      // chart, so use that instead of a dead size guide. An "a" code has no
      // twin: the others list a different cut.
      twinHandle(handle) {
        const match = /^([a-z]+\d+)(d?)$/.exec(`${handle ?? ''}`.trim().toLowerCase());
        if (!match) return '';
        return match[2] ? match[1] : `${match[1]}d`;
      }

      async fetchMetaobject() {
        const twin = this.twinHandle(this.dataset.handle);
        const response = await fetch(this.dataset.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.dataset.apiToken,
          },
          body: JSON.stringify({
            query: `
              query ProductSizeGuide($handle: String!, $twin: String!) {
                sizeGuideUnderscore: metaobject(handle: { type: "size_guide", handle: $handle }) { ...SizeGuide }
                sizeGuideHyphen: metaobject(handle: { type: "size-guide", handle: $handle }) { ...SizeGuide }
                sizeGuideTwin: metaobject(handle: { type: "size_guide", handle: $twin }) { ...SizeGuide }
              }
              fragment SizeGuide on Metaobject {
                handle
                type
                fields { key value reference { __typename ... on MediaImage { image { url altText width height } } } }
              }
            `,
            variables: { handle: this.dataset.handle, twin: twin || this.dataset.handle },
          }),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (payload.errors?.length) throw new Error(payload.errors.map((e) => e.message).join(', '));
        const metaobject =
          payload.data?.sizeGuideUnderscore || payload.data?.sizeGuideHyphen || (twin ? payload.data?.sizeGuideTwin : null);
        if (!metaobject) throw new Error(`Metaobject not found for handle "${this.dataset.handle}".`);
        return metaobject;
      }

      populate(metaobject) {
        const fields = new Map((metaobject.fields || []).map((field) => [field.key, field]));
        const measurementField = fields.get('measurements') || fields.get('measurments');
        const imageField = fields.get('image');
        const measurements = this.parseMeasurements(measurementField?.value);
        if (!measurements.length) throw new Error('Size guide measurements are empty.');

        this.measurements = measurements;
        const tableData = this.buildTableData(measurements);
        this.displayMeasurements = tableData.measurements;
        this.rows = tableData.rows;
        if (!this.displayMeasurements.length || !this.rows.length) throw new Error('Size guide table data is empty.');

        const image = imageField?.reference?.image || this.parseImageValue(imageField?.value);
        if (image?.url && this.imageElement && this.imageWrapElement) {
          this.imageElement.src = image.url;
          this.imageElement.alt = image.altText || fields.get('onlinecode')?.value || metaobject.handle || '';
          if (image.width) this.imageElement.width = image.width;
          if (image.height) this.imageElement.height = image.height;
          this.imageWrapElement.hidden = false;
        }

        if (this.unitsElement) {
          const activeTab = (this.chartTabs.find((t) => t.getAttribute('aria-selected') === 'true') || {});
          this.unitsElement.hidden = (activeTab.dataset && activeTab.dataset.sgCtab) === 'measure';
        }
        if (this.tableWrapElement) this.tableWrapElement.hidden = false;

        this.currentUnit = 'cm';
        this.updateUnitToggle();
        this.renderTable();
        this.buildMeasureList();
        this.setLoadedState();

        if (this.recommender) {
          try {
            const applicable = this.recommender.applyChart(this.measurements, { sizeOrder: this.sizeOrder });
            if (applicable && this.openRecBtn) this.openRecBtn.hidden = false;
            if (applicable && typeof this.recommender.maybeAutoCompute === 'function') {
              this.recommender.maybeAutoCompute();
            }
          } catch (error) {
            /* recommender stays dormant */
          }
        }
      }

      parseMeasurements(rawValue) {
        if (!rawValue) return [];
        try {
          const parsed = JSON.parse(rawValue);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error('Invalid size guide measurements JSON.', error);
          return [];
        }
      }

      parseImageValue(rawValue) {
        if (!rawValue) return null;
        try {
          const parsed = JSON.parse(rawValue);
          if (parsed?.url) return parsed;
        } catch (error) {
          return { url: rawValue };
        }
        return null;
      }

      buildRows(measurements) {
        const rowMap = new Map();
        const encounteredSizes = [];
        measurements.forEach((measurement) => {
          const label = measurement?.name?.trim();
          if (!label || !Array.isArray(measurement.values)) return;
          measurement.values.forEach((item) => {
            const size = this.normalizeSize(item?.name);
            if (!size) return;
            if (!rowMap.has(size)) {
              rowMap.set(size, {});
              encounteredSizes.push(size);
            }
            rowMap.get(size)[label] = item?.value ?? '';
          });
        });
        const orderedSizes = this.sizeOrder.filter((size) => rowMap.has(size));
        const extraSizes = encounteredSizes.filter((size) => !orderedSizes.includes(size));
        const sizes = [...orderedSizes, ...extraSizes];
        return sizes.map((size) => ({
          size,
          values: measurements.map((measurement) => ({
            label: measurement?.name?.trim() || '',
            value: rowMap.get(size)?.[measurement?.name?.trim() || ''] ?? '',
          })),
        }));
      }

      buildTableData(measurements) {
        const hasNamedSizes = measurements.some(
          (m) => Array.isArray(m?.values) && m.values.some((item) => item?.name)
        );
        if (hasNamedSizes) {
          this.unitConversionMode = 'all';
          return { measurements, rows: this.buildRows(measurements) };
        }
        this.unitConversionMode = 'selective';
        const axisMeasurement = this.pickSizeAxisMeasurement(measurements);
        const dataMeasurements = measurements.filter((m) => m !== axisMeasurement);
        const rows = (axisMeasurement?.values || []).map((axisValue, index) => ({
          size: `${axisValue?.value ?? axisValue?.name ?? index + 1}`.trim(),
          values: dataMeasurements.map((m) => ({
            label: m?.name?.trim() || '',
            value: m?.values?.[index]?.value ?? m?.values?.[index]?.name ?? '',
          })),
        }));
        return { measurements: dataMeasurements.length ? dataMeasurements : measurements, rows };
      }

      pickSizeAxisMeasurement(measurements) {
        const candidates = ['eu', 'size', 'sizes', 'us', 'uk'];
        for (const candidate of candidates) {
          const m = measurements.find((item) => item?.name?.trim()?.toLowerCase() === candidate);
          if (m) return m;
        }
        return measurements[0];
      }

      // fold separators + X-run spellings the same way the recommender does so
      // "L/XL" matches "L-XL" and "2XL" matches "XXL" (display stays original)
      canonSizeToken(size) {
        return this.normalizeSize(size)
          .replace(/[/\\.\-–—]+/g, '')
          .replace(/^(X{2,})([SL])$/, (m, xs, sl) => `${xs.length}X${sl}`);
      }

      renderTable() {
        if (!this.tableElement || !this.displayMeasurements.length || !this.rows.length) return;
        // mark the recommended row when the wizard has a result ("Your size")
        const recToken = this.recommender?.result?.recommended || null;
        const recCanon = recToken ? this.canonSizeToken(recToken) : null;
        const pillText = this.decodeEntities(this.measureI18n?.your_size || '');
        const headers = this.displayMeasurements
          .map((m) => `<th scope="col">${this.escapeHtml(this.displayMeasureName(m?.name))}</th>`)
          .join('');
        const rows = this.rows
          .map((row) => {
            const values = row.values
              .map((item) => `<td>${this.escapeHtml(this.formatValue(item.value, item.label))}</td>`)
              .join('');
            const isYourSize = recCanon && pillText && this.canonSizeToken(row.size) === recCanon;
            const pill = isYourSize ? `<span class="sg-your-size">${this.escapeHtml(pillText)}</span>` : '';
            return `<tr${isYourSize ? ' class="is-your-size"' : ''}><th scope="row">${this.escapeHtml(row.size)}${pill}</th>${values}</tr>`;
          })
          .join('');
        this.tableElement.innerHTML = `<thead><tr><th scope="col">Size</th>${headers}</tr></thead><tbody>${rows}</tbody>`;
      }

      normalizeSize(size) {
        if (!size) return '';
        return size.toString().trim().toUpperCase().replace(/\s+/g, '');
      }

      formatValue(rawValue, label = '') {
        const value = `${rawValue ?? ''}`.trim();
        if (!value) return '';
        const numericValue = Number.parseFloat(value);
        if (!Number.isFinite(numericValue)) return value;
        if (this.currentUnit === 'inch' && this.shouldConvertValue(label)) {
          return this.formatNumber(numericValue / 2.54);
        }
        return this.formatNumber(numericValue);
      }

      shouldConvertValue(label = '') {
        if (this.unitConversionMode === 'all') return true;
        const normalized = label.trim().toLowerCase();
        return ['cm', 'cms', 'centimeter', 'centimeters'].includes(normalized);
      }

      formatNumber(number) {
        const rounded = Math.round(number * 10) / 10;
        return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
      }

      setUnit(unit) {
        this.currentUnit = unit === 'inch' ? 'inch' : 'cm';
        this.updateUnitToggle();
        this.renderTable();
      }

      updateUnitToggle() {
        if (!this.unitButtons) return;
        const cur = this.currentUnit === 'inch' ? 'inch' : 'cm';
        this.unitButtons.forEach((button) => {
          const on = button.dataset.sizeGuideUnit === cur;
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          button.classList.toggle('is-active', on);
        });
      }

      // ---- chart modal tabs (Size Guide / How to Measure) ----
      setupChartTabs() {
        if (!this.chartTabs.length) return;
        this.chartTabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.selectChartTab(tab.dataset.sgCtab));
          tab.addEventListener('keydown', (event) => {
            let dir = 0;
            if (event.key === 'ArrowRight') dir = 1;
            else if (event.key === 'ArrowLeft') dir = -1;
            else return;
            event.preventDefault();
            const next = this.chartTabs[(index + dir + this.chartTabs.length) % this.chartTabs.length];
            next.focus();
            this.selectChartTab(next.dataset.sgCtab);
          });
        });
      }

      selectChartTab(name) {
        this.chartTabs.forEach((tab) => {
          const on = tab.dataset.sgCtab === name;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
        });
        this.chartPanels.forEach((panel) => {
          panel.hidden = panel.dataset.sgCpanel !== name;
        });
        // the cm/in unit toggle only applies to the Size Guide table
        if (this.unitsElement) {
          this.unitsElement.hidden = !(name === 'guide' && this.hasLoaded);
        }
      }

      // ---- chart modal open/close ----
      setupChartModalControls() {
        if (!this.chartModal) return;
        this.querySelector('[data-sg-close]')?.addEventListener('click', () => this.closeChart());
        // close on backdrop click (container or overlay), not on dialog clicks
        this.chartModal.addEventListener('click', (event) => {
          if (event.target === this.chartModal || event.target.hasAttribute('data-sg-overlay')) {
            this.closeChart();
          }
        });
      }

      openChart(tab) {
        if (!this.chartModal) return;
        try {
          (window.dataLayer = window.dataLayer || []).push({ event: 'size_chart_open', sc_tab: tab || 'guide' });
        } catch (error) {
          /* analytics only */
        }
        // Portal to <body> so position:fixed + overlay are not constrained by a
        // transformed/positioned ancestor (the product section).
        if (this.chartModal.parentElement !== document.body) {
          document.body.appendChild(this.chartModal);
        }
        this.openedBy = document.activeElement;
        this.chartModal.hidden = false;
        document.body.style.overflow = 'hidden';
        if (tab) this.selectChartTab(tab);
        this.ensureLoaded().catch(() => {});
        document.addEventListener('keydown', this.onKeydown);
        if (typeof trapFocus === 'function') trapFocus(this.chartDialog || this.chartModal, this.chartDialog);
      }

      closeChart() {
        if (!this.chartModal || this.chartModal.hidden) return;
        this.chartModal.hidden = true;
        document.removeEventListener('keydown', this.onKeydown);
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
        this.releaseBodyScroll();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeChart();
        }
      }

      // shared: only release body scroll if no sibling modal is open
      releaseBodyScroll() {
        const recOpen = this.recommender && this.recommender.isOpen && this.recommender.isOpen();
        const chartOpen = this.chartModal && !this.chartModal.hidden;
        if (!recOpen && !chartOpen) document.body.style.overflow = '';
      }

      // recommender result -> update the "Find my size" link to "Your size is X"
      setRecommendedLabel(text) {
        if (this.openRecBtn && text) {
          this.openRecBtn.textContent = text;
          this.openRecBtn.classList.add('product-size__link--has-result');
        }
        // refresh an already-rendered chart table so its "Your size" row marker
        // picks up the fresh recommendation
        if (this.hasLoaded) this.renderTable();
      }

      setLoadingState() {
        if (this.loadingElement) this.loadingElement.hidden = false;
        if (this.errorElement) this.errorElement.hidden = true;
        if (this.contentElement) this.contentElement.hidden = true;
        if (this.fallbackElement) this.fallbackElement.hidden = true;
      }

      setLoadedState() {
        if (this.loadingElement) this.loadingElement.hidden = true;
        if (this.errorElement) this.errorElement.hidden = true;
        if (this.contentElement) this.contentElement.hidden = false;
        if (this.fallbackElement) this.fallbackElement.hidden = true;
      }

      setErrorState() {
        if (this.loadingElement) this.loadingElement.hidden = true;
        if (this.contentElement) this.contentElement.hidden = true;
        // fall back to the page-content guide if one was provided
        if (this.fallbackElement) {
          this.fallbackElement.hidden = false;
          if (this.errorElement) this.errorElement.hidden = true;
        } else if (this.errorElement) {
          this.errorElement.hidden = true;
        }
      }

      escapeHtml(value) {
        return value
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#39;');
      }

      // Shopify's `| t` filter HTML-escapes its output (e.g. ' -> &#39;), so the
      // measure descriptions arrive entity-encoded; decode once before render.
      decodeEntities(value) {
        const str = `${value ?? ''}`;
        if (str.indexOf('&') === -1) return str;
        const ta = document.createElement('textarea');
        ta.innerHTML = str;
        return ta.value;
      }

      // Normalised chart-column name used for every lookup: lowercased, single
      // spaces, and the "Fringle length" typo that ships in a handful of charts.
      normalizeMeasureName(rawName) {
        return `${rawName ?? ''}`.trim().toLowerCase().replace(/\s+/g, ' ').replace('fringle', 'fringe');
      }

      // Localized display name for a chart column (table header + measure list).
      // Looks up the normalised English column name; falls back to the raw name
      // so unmapped/accessory columns (and English stores) pass through unchanged.
      displayMeasureName(rawName) {
        const n = `${rawName ?? ''}`.trim();
        if (!n) return '';
        const loc = this.measureNames && this.measureNames[this.normalizeMeasureName(n)];
        return loc ? this.decodeEntities(loc) : n;
      }

      // What the chart describes, decided from its own columns: a top/coat
      // (chest, shoulder or sleeve columns), a bottom (hip, thigh or waist
      // columns) or a flat item such as a scarf, throw or hat. A "Length" /
      // "Body length" column means something different in each case.
      chartKind(columns) {
        const names = (columns || []).map((m) => this.normalizeMeasureName(m?.name));
        const has = (re) => names.some((n) => re.test(n));
        if (has(/chest|bust|shoulder|sleeve/)) return 'top';
        if (has(/hip|thigh|waist/)) return 'bottom';
        return 'item';
      }

      // Map a chart column name to a known "how to measure" description key.
      // Unknown construction columns (hem, brim, foot…) get no description
      // rather than a wrong one.
      measureKeyFor(rawName, kind = 'top', fringed = false) {
        const name = this.normalizeMeasureName(rawName);
        if (!name) return null;
        if (name.includes('fringe')) return 'fringe_length';
        if (name.includes('shoulder')) return 'shoulder_width';
        if (name.includes('sleeve')) return name.includes('length') ? 'sleeve_length' : 'sleeve_width';
        if (name.includes('chest') || name.includes('bust')) return 'chest_width';
        if (name.includes('waist')) return 'waist_width';
        if (name.includes('hip')) return 'hip_width';
        if (name.includes('thigh')) return 'thigh_width';
        if (name.includes('body') || name === 'length') {
          if (kind === 'bottom') return 'bottom_length'; // trousers/skirts: waistband to hem
          if (kind === 'top' || name.includes('body')) return 'body_length'; // shoulder to hem
          return fringed ? 'length_fringed' : 'length'; // flat item: edge to edge
        }
        if (name === 'width') return 'width';
        return null;
      }

      // Build the "How to Measure" bulleted list from the chart's OWN columns,
      // in table order (the same order as the diagram image).
      buildMeasureList() {
        if (!this.measureListEl) return;
        const columns = this.displayMeasurements || [];
        const steps = this.measureI18n || {};
        const kind = this.chartKind(columns);
        const fringed = columns.some((m) => this.normalizeMeasureName(m?.name).includes('fringe'));
        const items = columns
          .map((measurement) => {
            const name = measurement?.name?.trim() || '';
            if (!name) return '';
            const key = this.measureKeyFor(name, kind, fringed);
            const desc = key && steps[key] ? this.decodeEntities(steps[key]) : '';
            return (
              `<li class="sg-measure__item">` +
              `<span class="sg-measure__text"><strong>${this.escapeHtml(this.displayMeasureName(name))}</strong>` +
              (desc ? ` — ${this.escapeHtml(desc)}` : '') +
              `</span></li>`
            );
          })
          .filter(Boolean);
        this.measureListEl.innerHTML = items.join('');
        if (this.measureIntroEl) {
          // "Lay a similar garment flat…" reads wrong above a scarf or throw
          const intro = (kind === 'item' && steps.intro_accessory) || steps.intro;
          if (intro) this.measureIntroEl.textContent = this.decodeEntities(intro);
          this.measureIntroEl.hidden = items.length === 0;
        }
      }
    }
  );
}
